import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Search } from 'lucide-react';

const NAV = [
  { label: 'Collection', to: '/products' },
  { label: 'Made to Order', to: '/products?category=made-to-order' },
  { label: 'About', to: '/about' },
  { label: 'Visit', to: '/contact' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

  const isActive = (to) => {
    const [path, query] = to.split('?');
    if (query) return location.pathname === path && location.search.includes(query);
    return location.pathname === path;
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        scrolled
          ? 'bg-[rgba(247,239,224,0.85)] backdrop-blur-xl border-b border-[color:var(--mb-border)] py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 sm:px-10">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img
            src="/brand/mark.jpg"
            alt="Mahabir Bastralaya"
            className="h-11 sm:h-12 w-auto"
            style={{ mixBlendMode: 'multiply' }}
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-serif-display text-xl tracking-tight text-[color:var(--mb-primary)] lowercase" style={{ fontWeight: 500 }}>
              mahabir
            </span>
            <span className="font-serif-display text-xl tracking-tight text-[color:var(--mb-primary)] lowercase -mt-1" style={{ fontWeight: 500 }}>
              Bastralaya
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`nav-link text-[0.7rem] uppercase tracking-[0.28em] font-medium ${isActive(n.to) ? 'active' : ''}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="search-btn"
            onClick={() => navigate('/products')}
            className="hidden sm:flex w-10 h-10 items-center justify-center rounded-full border border-[color:var(--mb-border)] text-[color:var(--mb-text)] hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)]"
            style={{ transition: 'border-color 300ms ease, color 300ms ease' }}
            aria-label="Search"
          >
            <Search size={16} />
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-[color:var(--mb-border)] text-[color:var(--mb-text)]"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="md:hidden bg-[color:var(--mb-bg)] border-t border-[color:var(--mb-border)] px-6 py-8 flex flex-col gap-6"
            data-testid="mobile-menu"
          >
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-2xl font-serif-display tracking-tight"
              >
                {n.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
